import store from '../../../vuex'
import api from "@/api";
import components from '@/components'

export class AudioController {
    constructor() {
        this.audio = new Audio()
        this.handle = -1
        this.volume = 0 // 先设置默认值
        this.fadeDuration = 200
        this.buffer = new Audio()

        // DLNA相关属性 - 新增
        this.playMode = 'local' // 'local' | 'dlna'
        this.dlnaDevice = null  // 当前选中的DLNA设备
        this.dlnaStatusTimer = null // 状态轮询定时器
        this.dlnaStatusInterval = 30000 // 状态更新间隔(ms)
        this.dlnaCurrentTime = 0 // DLNA播放位置
        this.dlnaDuration = 0 // DLNA音轨时长
        this.dlnaVolume = 50 // DLNA设备音量

        // 在store初始化后再获取volume
        setTimeout(() => {
            this.volume = store.state.common.volume
        }, 0)

        this.ontimeupdate = (value) => {
            store.commit('setCurrentTime', value)
        }
        this.ondurationchange = (value) => {
            store.commit('setDuration', value)
        }
        this.onpause = () => {
            store.commit('setPaused')
        }
        this.onstreamtitlechange = (value) => {
            store.commit('setStreamTitle', value)
        }
        this.onended = () => {
            if (store.getters['hasNext'] || store.state.common.repeat) {
                return store.dispatch('next')
            } else {
                return store.dispatch('resetQueue')
            }
        }
        this.onerror = (error) => { 
            console.error('音频错误:', error)
            
            // 显示错误弹窗给用户
            const errorMessage = this.getErrorMessage(error)
            components.myMsg.notify({
                title: '播放失败！',
                content: errorMessage,
                type: 'error',
                time: 4000
            })
            
            // 错误时自动播放下一首
            if (store.getters['hasNext']) {
                console.log('音频播放出错，自动播放下一首')
                store.dispatch('next')
            } else {
                console.log('音频播放出错，没有下一首，重置播放队列')
                store.dispatch('resetQueue')
            }
        }

        // 初始化媒体会话控制
        if ('mediaSession' in navigator) {
            navigator.mediaSession.setActionHandler('play', () => {
                this.resume()
            })
            navigator.mediaSession.setActionHandler('pause', () => {
                this.pause()
            })
            navigator.mediaSession.setActionHandler('previoustrack', () => {
                store.dispatch('previous')
            })
            navigator.mediaSession.setActionHandler('nexttrack', () => {
                store.dispatch('next')
            })
        }
    }

    // 获取用户友好的错误消息
    getErrorMessage(error) {
        if (!error) return '未知错误'
        
        // HTML5 Audio 错误码映射
        if (error.code) {
            switch (error.code) {
                case 1: // MEDIA_ERR_ABORTED
                    return '播放被中断'
                case 2: // MEDIA_ERR_NETWORK  
                    return '网络连接错误，请检查网络连接'
                case 3: // MEDIA_ERR_DECODE
                    return '音频文件解码失败，可能文件损坏'
                case 4: // MEDIA_ERR_SRC_NOT_SUPPORTED
                    return '浏览器不支持播放该音频格式或文件目录被移动后已不存在！'
                default:
                    return `播放错误（错误码：${error.code}）`
            }
        }
        
        // 其他错误类型
        if (error instanceof Error) {
            return error.message || '播放异常'
        }
        
        return '播放失败，请重试'
    }

    // 新增：设置播放模式
    setPlayMode(mode, device = null) {
        console.log(`切换播放模式: ${this.playMode} -> ${mode}`, device)
        
        const oldMode = this.playMode
        this.playMode = mode
        this.dlnaDevice = device
        
        if (mode === 'dlna' && device) {
            this.startDlnaStatusPolling()
        } else {
            this.stopDlnaStatusPolling()
            if (oldMode === 'dlna') {
                console.log('已断开DLNA连接，切换到本地播放')
            }
        }
        
        // 通知store播放模式变化
        store.commit('setPlayMode', mode)
        if (device) {
            store.commit('setDlnaDevice', device)
        }
    }

    // 新增：获取当前播放模式
    getPlayMode() {
        return this.playMode
    }

    // 新增：检查是否为DLNA模式
    isDlnaMode() {
        return this.playMode === 'dlna' && this.dlnaDevice
    }

    currentTime() {
        // DLNA模式返回DLNA设备的播放位置
        if (this.isDlnaMode()) {
            return this.dlnaCurrentTime
        }
        return this.audio.currentTime
    }

    duration() {
        // DLNA模式返回DLNA音轨时长
        if (this.isDlnaMode()) {
            return this.dlnaDuration
        }
        return this.audio.duration
    }

    setBuffer(url) {
        // DLNA模式下不需要缓冲
        if (this.isDlnaMode()) {
            return
        }
        this.buffer.src = url
    }

    setVolume(value) {
        this.cancelFade()
        this.volume = value
        
        if (this.isDlnaMode()) {
            // DLNA模式下同时设置设备音量
            this.setDlnaVolume(Math.round(value * 100))
        } else {
            // 本地播放模式
            this.audio.volume = value
        }
    }

    async pause() {
        if (this.isDlnaMode()) {
            return this.dlnaPause()
        }
        
        // 原有本地播放逻辑
        await this.fadeOut()
        this.audio.pause()
    }

    async resume() {
        console.log('resume')
        if (this.isDlnaMode()) {
            return this.dlnaResume()
        }
        
        // 原有本地播放逻辑
        // 如果音频还没有加载（preload=none），先加载
        if (this.audio.preload === 'none') {
            this.audio.preload = 'auto'
            this.audio.load() // 重新加载音频
        }
        
        this.audio.volume = 0.0
        await this.audio.play()
        this.fadeIn()
    }

    async seek(value, unit='%') {
        // 检查音频是否已加载且有有效的时长
        if (!this.audio || !isFinite(this.audio.duration) || this.audio.duration <= 0) {
            console.warn('无法跳转：音频未加载或时长无效')
            return
        }

        let absoluteTime = 0
        if (unit === '%') {
            // 将相对进度值转换为绝对时间
            absoluteTime = value * this.audio.duration
        } else if (unit === 's') {
            absoluteTime = value
        }
        
        // 再次检查计算出的时间是否有效
        if (!isFinite(absoluteTime) || absoluteTime < 0) {
            console.warn('无法跳转：计算出的时间无效', { value, duration: this.audio.duration, absoluteTime })
            return
        }

        // 确保时间不超过音频总时长
        const safeTime = Math.min(absoluteTime, this.audio.duration)
        this.audio.currentTime = safeTime

        if (this.isDlnaMode()) {
            return this.dlnaSeek(safeTime)
        }
        
        // 原有本地播放逻辑
        await this.fadeOut(this.fadeDuration / 2.0)
        this.audio.volume = 0.0
        
        await this.fadeIn(this.fadeDuration / 2.0)
    }

    async changeTrack(options) {

        if (this.isDlnaMode()) {
            return this.dlnaChangeTrack(options)
        }
        
        // 原有本地播放逻辑保持不变
        if (this.audio) {
            this.cancelFade()
            endPlayback(this.audio, this.fadeDuration)
        }

        // 立即更新媒体会话元数据
        this.updateMediaSession(options)

        this.audio = new Audio()
        
        // 如果需要暂停加载，先设置preload为none并静音
        if (options.paused === true) {
            this.audio.preload = 'none'
            this.audio.volume = 0.0
        }
        
        this.audio.onerror = () => {
            this.onerror(this.audio.error)
        }
        this.audio.onended = () => {
            this.onended()
        }
        this.audio.ontimeupdate = () => {
            this.ontimeupdate(this.audio.currentTime)
        }
        this.audio.ondurationchange = () => {
            this.ondurationchange(this.audio.duration)
        }
        this.audio.onpause = () => {
            this.onpause()
        }
        
        // 设置音频源
        this.audio.src = options.url
        
        this.ondurationchange(this.audio.duration)
        this.ontimeupdate(this.audio.currentTime)
        this.onstreamtitlechange(null)
        
        // 对于非暂停模式，设置初始音量为0
        if (options.paused !== true) {
            this.audio.volume = 0.0
        }

        if (options.paused !== true) {
            try {
                await this.audio.play()
                api.subsonic.scrobble(options.id)
            } catch (error) {
                if (error instanceof Error && error.name === 'AbortError') {
                    console.warn(error)
                    return
                }
                throw error
            }
            this.fadeIn()
        } else {
            // 当 paused: true 时，确保音频完全暂停且静音
            this.audio.pause()
            // 在短时间内保持静音，避免任何意外播放
            setTimeout(() => {
                if (this.audio.paused) {
                    this.audio.volume = 0.0 // 保持静音直到用户主动播放
                }
            }, 50)
        }
    }

    // 新增：提取媒体会话更新逻辑
    updateMediaSession(options) {
        if ('mediaSession' in navigator) {
            // 确保所有必需字段都有有效值
            const title = options.title || options.name || '未知歌曲'
            const artist = options.artist || options.artistName || '未知艺术家'
            const album = options.album || options.albumName || '未知专辑'
            const artwork = options.artwork || options.image || options.coverArt

            try {
                const metadata = new MediaMetadata({
                    title,
                    artist,
                    album,
                    artwork: artwork ? [
                        { src: artwork, sizes: '512x512', type: 'image/jpeg' }
                    ] : [
                        // 提供一个默认封面
                        { src: '/assets/default-cover.jpg', sizes: '512x512', type: 'image/jpeg' }
                    ]
                })
                navigator.mediaSession.metadata = metadata
                navigator.mediaSession.playbackState = options.paused ? 'paused' : 'playing'
            } catch (error) {
                console.error('Media Session Update Error:', error)
            }
        }
    }

    // ==================== DLNA相关方法 ====================

    // DLNA播放控制
    async dlnaResume() {
        console.log('dlnaResume')
        try {
            const result = await api.dlna.control(this.dlnaDevice.id, 'play')
            if (result.success) {
                console.log('DLNA设备开始播放')
                
                // 同步更新浏览器端的播放状态
                // 1. 通知store更新播放状态
                store.commit('setPlaying')
                
                // 2. 更新媒体会话状态
                if ('mediaSession' in navigator) {
                    navigator.mediaSession.playbackState = 'playing'
                }
                
                // 3. 确保DLNA状态轮询正在运行（用于同步播放进度）
                if (!this.dlnaStatusTimer) {
                    this.startDlnaStatusPolling()
                }
                
                console.log('DLNA播放状态已同步到浏览器')
            } else {
                console.error('DLNA播放失败:', result.error)
                // 显示DLNA错误弹窗
                components.myMsg.notify({
                    title: 'DLNA播放失败！',
                    content: result.error || 'DLNA设备播放失败，请检查设备连接',
                    type: 'error',
                    time: 4000
                })
                this.onerror(new Error(result.error || 'DLNA播放失败'))
            }
            return result.success
        } catch (error) {
            console.error('DLNA播放异常:', error)
            this.onerror(error)
            return false
        }
    }

    async dlnaPause() {
        try {
            const result = await api.dlna.control(this.dlnaDevice.id, 'pause')
            if (result.success) {
                console.log('DLNA设备已暂停')
                this.onpause()
            } else {
                console.error('DLNA暂停失败:', result.error)
                // 显示DLNA暂停错误弹窗
                components.myMsg.notify({
                    title: 'DLNA暂停失败！',
                    content: result.error || 'DLNA设备暂停失败，请检查设备连接',
                    type: 'error',
                    time: 4000
                })
                this.onerror(new Error(result.error || 'DLNA暂停失败'))
            }
            return result.success
        } catch (error) {
            console.error('DLNA暂停异常:', error)
            this.onerror(error)
            return false
        }
    }

    async dlnaSeek(value) {
        try {
            console.log(`DLNA设备跳转到: ${value}秒`)
            
            // 立即更新本地状态以提供即时反馈
            this.dlnaCurrentTime = value
            this.ontimeupdate(value)
            
            // 调用DLNA API进行实际的seek操作
            const result = await api.dlna.control(this.dlnaDevice.id, 'seek', null, { position: value })
            
            if (result.success) {
                console.log('DLNA设备跳转成功')
                return true
            } else {
                console.error('DLNA跳转失败:', result.error)
                // 显示DLNA跳转错误弹窗
                components.myMsg.notify({
                    title: 'DLNA跳转失败！',
                    content: result.error || 'DLNA设备跳转失败，请检查设备连接',
                    type: 'error',
                    time: 4000
                })
                this.onerror(new Error(result.error || 'DLNA跳转失败'))
                return false
            }
        } catch (error) {
            console.error('DLNA跳转异常:', error)
            this.onerror(error)
            return false
        }
    }

    async dlnaChangeTrack(options) {
        try {
            console.log('DLNA切换音轨:', options)
            
            // 立即更新媒体会话元数据
            this.updateMediaSession(options)
            
            // 推送音轨到DLNA设备
            const result = await api.dlna.pushTrack(
                this.dlnaDevice.id, 
                options.id, 
                options.currentTime || 0
            )
            
            if (result.success) {
                console.log('音轨已推送到DLNA设备')
                
                // 更新DLNA状态
                this.dlnaDuration = options.duration || 0
                this.dlnaCurrentTime = options.currentTime || 0
                console.log('dlnaDuration:', this.dlnaDuration)
                console.log('dlnaCurrentTime:', this.dlnaCurrentTime)
                // 触发相关回调
                this.ondurationchange(this.dlnaDuration)
                this.ontimeupdate(this.dlnaCurrentTime)
                this.onstreamtitlechange(options.title || options.name)
                
                // 根据播放状态控制DLNA设备
                if (options.paused !== true) {
                    await this.dlnaResume()
                    // 记录播放
                    api.subsonic.scrobble(options.id)
                } else {
                    console.log('dlnaChangeTrack: 保持DLNA暂停状态, options.paused=', options.paused)   
                    this.dlnaPause()
                    // 暂停状态时不启动状态轮询，避免错误状态同步
                    this.stopDlnaStatusPolling()
                }
            } else {
                console.error('推送音轨到DLNA设备失败:', result.error)
                // 显示DLNA切换音轨错误弹窗
                components.myMsg.notify({
                    title: 'DLNA切换失败！',
                    content: result.error || '推送音轨到DLNA设备失败，请检查设备连接',
                    type: 'error',
                    time: 4000
                })
                this.onerror(new Error(result.error || '推送音轨失败'))
            }
            
            return result.success
        } catch (error) {
            console.error('DLNA切换音轨异常:', error)
            this.onerror(error)
            return false
        }
    }

    // DLNA音量控制
    async setDlnaVolume(volume) {
        try {
            const result = await api.dlna.setVolume(this.dlnaDevice.id, volume)
            if (result.success) {
                this.dlnaVolume = volume
                console.log(`DLNA设备音量已设置为: ${volume}%`)
            } else {
                console.error('设置DLNA音量失败:', result.error)
            }
            return result.success
        } catch (error) {
            console.error('设置DLNA音量异常:', error)
            return false
        }
    }

    // DLNA状态轮询
    startDlnaStatusPolling() {
        this.stopDlnaStatusPolling()
        
        // 立即执行一次状态获取
        this.getDlnaStatus()
        
        // 设置定时器进行后续轮询
        this.dlnaStatusTimer = setInterval(async () => {
            this.getDlnaStatus()
        }, this.dlnaStatusInterval)
    }

    // 提取DLNA状态获取逻辑为独立方法
    async getDlnaStatus() {
        try {
            const status = await api.dlna.getStatus(this.dlnaDevice.udn)
            if (status.success) {
                this.updateStatusFromDlna(status.status)
            }
        } catch (error) {
            console.warn('DLNA状态获取失败:', error)
        }
    }

    stopDlnaStatusPolling() {
        if (this.dlnaStatusTimer) {
            clearInterval(this.dlnaStatusTimer)
            this.dlnaStatusTimer = null
            console.log('DLNA状态轮询已停止')
        }
    }

    // 根据DLNA状态更新本地状态
    updateStatusFromDlna(dlnaStatus) {
        try {
            // 更新播放位置
            if (dlnaStatus.position !== undefined) {
                const serverTime = dlnaStatus.position
                // 同步真实的播放位置，停止前端模拟
                if (Math.abs(this.dlnaCurrentTime - serverTime) > 2) {
                    console.log(`同步DLNA播放位置: ${this.dlnaCurrentTime} -> ${serverTime}`)
                    this.dlnaCurrentTime = serverTime
                    this.ontimeupdate(serverTime)
                }
            }
            
            // 更新时长
            if (dlnaStatus.duration !== undefined) {
                const duration = dlnaStatus.duration
                if (duration !== this.dlnaDuration) {
                    this.dlnaDuration = duration
                    this.ondurationchange(duration)
                }
            }
            
            // 播放状态检测 - 关键改进
            if (dlnaStatus.transport_state) {
                if (dlnaStatus.transport_state === 'STOPPED' || dlnaStatus.transport_state === 'IDLE') {
                    // 检查是否是自然播放结束
                    const isNaturalEnd = this.dlnaDuration > 0 && 
                                       this.dlnaCurrentTime >= this.dlnaDuration - 3 // 允许3秒误差
                    
                    if (isNaturalEnd) {
                        console.log('检测到DLNA播放自然结束，触发下一首')
                        this.onended() // 触发播放结束逻辑
                    } else {
                        console.log('DLNA手动停止，不触发下一首')
                        this.onpause()
                    }
                } else if (dlnaStatus.transport_state === 'PAUSED') {
                    this.onpause()
                }else if (dlnaStatus.transport_state === 'PLAYING') {
                    // 设备正在播放时，同步播放状态
                    if ('mediaSession' in navigator) {
                        navigator.mediaSession.playbackState = 'playing'
                    }
                }
            }
            
            // 更新音量
            if (dlnaStatus.volume !== undefined && dlnaStatus.volume !== this.dlnaVolume) {
                this.dlnaVolume = dlnaStatus.volume
                this.volume = dlnaStatus.volume / 100
            }
        } catch (error) {
            console.error('更新DLNA状态失败:', error)
        }
    }

    // 时间格式解析 (HH:MM:SS -> 秒)
    parseTimeToSeconds(timeString) {
        try {
            if (!timeString || timeString === '00:00:00') return 0
            
            const parts = timeString.split(':')
            if (parts.length === 3) {
                const hours = parseInt(parts[0]) || 0
                const minutes = parseInt(parts[1]) || 0
                const seconds = parseInt(parts[2]) || 0
                return hours * 3600 + minutes * 60 + seconds
            }
            return 0
        } catch (error) {
            console.error('时间格式解析失败:', timeString, error)
            return 0
        }
    }

    // 清理资源
    destroy() {
        this.stopDlnaStatusPolling()
        this.cancelFade()
        
        if (this.audio) {
            this.audio.pause()
            this.audio = null
        }
    }

    // ==================== 原有方法保持不变 ====================

    cancelFade() {
        clearTimeout(this.handle)
    }

    fadeIn(duration = this.fadeDuration) {
        // DLNA模式下不需要fade效果
        if (this.isDlnaMode()) {
            return Promise.resolve()
        }
        return this.fadeFromTo(0.0, this.volume, duration)
    }

    fadeOut(duration = this.fadeDuration) {
        // DLNA模式下不需要fade效果
        if (this.isDlnaMode()) {
            return Promise.resolve()
        }
        return this.fadeFromTo(this.volume, 0.0, duration)
    }

    fadeFromTo(from, to, duration) {
        // DLNA模式下不需要fade效果
        if (this.isDlnaMode()) {
            return Promise.resolve()
        }
        
        const startTime = Date.now()
        const step = (to - from) / duration
        if (duration <= 0.0) {
            this.audio.volume = to
        }
        clearTimeout(this.handle)
        return new Promise((resolve) => {
            const run = () => {
                if (this.audio.volume === to) {
                    resolve()
                    return
                }
                const elapsed = Date.now() - startTime
                this.audio.volume = clamp(0.0, this.volume, from + (elapsed * step))
                this.handle = setTimeout(run, 10)
            }
            run()
        })
    }
}

function endPlayback(audio, duration) {
    async function fade(audio, from, to, duration) {
        if (duration <= 0.0) {
            audio.volume = to
            return audio
        }
        const startTime = Date.now()
        const step = (to - from) / duration
        while (audio.volume !== to) {
            const elapsed = Date.now() - startTime
            audio.volume = clamp(0.0, 1.0, from + (elapsed * step))
            await sleep(10)
        }
        return audio
    }
    audio.ontimeupdate = null
    audio.ondurationchange = null
    audio.onpause = null
    audio.onerror = null
    audio.onended = null
    audio.onloadedmetadata = null
    fade(audio, audio.volume, 0.0, duration)
        .catch((err) => console.warn('Error during fade out: ' + err.stack))
        .finally(() => {
            audio.pause()
        })
}

function sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms))
}

function clamp(min, max, value) {
    return Math.max(min, Math.min(value, max))
}
